// The official name of the risk rating of a client record (SEP-318, firm
// decision 1).
//
// Dowgate has five official risk categories: Low, Moderately Low, Moderate,
// Moderately High and High. The dashboard and the Suitability Assessment must
// use the same names.
//
// The `Client.riskRating` enum keeps its five stored values (`Conservative`,
// `Cautious`, `Balanced`, `Growth` and `Adventurous`). The imports write them
// from the firm's source categories ("Moderately Low" gives `Cautious`). This
// module is the reverse of that map. It gives the rung of the profile ladder
// for a stored value, and the official name for the screen. A stored value is
// therefore never a name that a person reads.
//
// This module maps the client risk rating only. It never maps the 1 to 7 band
// of the fact-find risk questionnaire to a profile: the firm did not approve
// such a map (`intended-risk-separation-guard.test.ts`, G4).

import { RISK_PROFILE_LADDER, riskProfileLabel, type LadderRiskProfile } from './risk-profile';

/** The stored values of `Client.riskRating`, from the lowest risk to the highest. */
export const CLIENT_RISK_RATING_VALUES = [
    'Conservative',
    'Cautious',
    'Balanced',
    'Growth',
    'Adventurous',
] as const;

export type ClientRiskRating = (typeof CLIENT_RISK_RATING_VALUES)[number];

/**
 * The rung of the profile ladder for a client risk rating. The function also
 * accepts the official name ("Moderately Low Risk") and the source category of
 * the firm ("Moderately Low"), so a raw import value reads the same. A value
 * outside these gives null.
 */
export function ladderProfileOfRating(rating: string | null | undefined): LadderRiskProfile | null {
    const value = rating
        ?.trim()
        .toLowerCase()
        .replace(/\s+risk$/, '');
    switch (value) {
        case 'conservative':
        case 'low':
            return 'low';
        case 'cautious':
        case 'moderately low':
            return 'moderately_low';
        case 'balanced':
        case 'moderate':
            return 'moderate';
        case 'growth':
        case 'moderately high':
            return 'moderately_high';
        case 'adventurous':
        case 'high':
            return 'high';
        default:
            return null;
    }
}

/**
 * The official name of a client risk rating, for example "Moderately Low Risk".
 * A value that no rung matches comes back trimmed, so the screen never hides
 * data that it cannot name. An empty value gives null.
 */
export function clientRiskRatingLabel(rating: string | null | undefined): string | null {
    const trimmed = rating?.trim();
    if (!trimmed) return null;
    const profile = ladderProfileOfRating(trimmed);
    return profile ? riskProfileLabel(profile) : trimmed;
}

/**
 * The sort position of a client risk rating: 0 for Low Risk to 4 for High
 * Risk. A value that no rung matches sorts after the five.
 */
export function clientRiskRatingRank(rating: string | null | undefined): number {
    const profile = ladderProfileOfRating(rating);
    return profile ? RISK_PROFILE_LADDER.indexOf(profile) : RISK_PROFILE_LADDER.length;
}

/**
 * The options of a control that chooses a client risk rating. The value is the
 * stored enum value, and the label is the official name.
 */
export const CLIENT_RISK_RATING_OPTIONS: ReadonlyArray<{
    readonly value: ClientRiskRating;
    readonly label: string;
}> = CLIENT_RISK_RATING_VALUES.map((value) => ({
    value,
    label: clientRiskRatingLabel(value) ?? value,
}));
